import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Grid, TextField, MenuItem, Button,
  Typography, IconButton, Table, TableHead, TableRow, TableCell,
  TableBody, Alert, Divider, Chip
} from '@mui/material';
import { Add, Delete, Save, PictureAsPdf } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import PageHeader from '../common/PageHeader';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CNY', 'JPY', 'AED', 'SGD', 'HKD'];
const PURPOSES = [
  'Import of Goods - Advance Payment',
  'Import of Goods - Against Invoice',
  'Import of Raw Materials',
  'Import of Electronic Components',
  'Import of Machinery Parts',
];

const EMPTY_ITEM = { description: '', sku: '', hsn_code: '', quantity: '', unit: 'PCS', unit_price: '', amount: '' };

export default function CreatePO() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const fromPi = searchParams.get('from_pi');   // "Create PO" from the proforma list
  const navigate = useNavigate();
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [proformas, setProformas] = useState([]);
  const [sourcePi, setSourcePi] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    po_number: '', po_date: today, ship_date: '',
    payment_terms: '100% in Advance',
    beneficiary_id: '', currency: 'USD', exchange_rate: '',
    inr_amount: '', purpose_of_remittance: PURPOSES[0], notes: ''
  });
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);

  useEffect(() => {
    api.get('/beneficiaries').then(res => setBeneficiaries(res.data.data));
    if (!id) {
      api.get('/purchase-orders/next-number').then(res =>
        setForm(f => ({ ...f, po_number: res.data.data.po_number }))
      );
      api.get('/invoices').then(r => setProformas(r.data.data || []));
    }
    if (id) {
      api.get(`/purchase-orders/${id}`).then(res => {
        const d = res.data.data;
        setForm({
          po_number: d.po_number, po_date: d.po_date,
          ship_date: d.ship_date || '', payment_terms: d.payment_terms || '100% in Advance',
          beneficiary_id: d.beneficiary_id,
          currency: d.currency, exchange_rate: d.exchange_rate || '',
          inr_amount: d.inr_amount || '', purpose_of_remittance: d.purpose_of_remittance || '',
          notes: d.notes || ''
        });
        if (d.items?.length) setItems(d.items.map(i => ({
          description: i.description, sku: i.sku || '', hsn_code: i.hsn_code || '',
          quantity: i.quantity, unit: i.unit || 'PCS',
          unit_price: i.unit_price, amount: i.amount
        })));
      });
    }
  }, [id]);

  // Prefill this PO from a proforma invoice. The PO's OWN fields are untouched:
  // po_number keeps its auto-generated PO/<FY>/… value and po_date stays today —
  // only the supplier, commercial terms and line items are copied across.
  const applyProforma = async (pid) => {
    setSourcePi(pid ? String(pid) : '');
    if (!pid) return;
    try {
      const r = await api.get(`/invoices/${pid}`);
      const d = r.data.data || {};
      setForm(f => ({
        ...f,
        beneficiary_id:        d.beneficiary_id ?? f.beneficiary_id,
        currency:              d.currency || f.currency,
        exchange_rate:         d.exchange_rate ?? '',
        inr_amount:            d.inr_amount ?? '',
        payment_terms:         d.payment_terms || f.payment_terms,
        purpose_of_remittance: d.purpose_of_remittance || f.purpose_of_remittance,
        ship_date:             d.ship_date || f.ship_date,
        notes:                 d.notes || f.notes,
      }));
      if (d.items?.length) {
        setItems(d.items.map(i => ({
          description: i.description || '',
          sku:         i.sku || '',
          hsn_code:    i.hsn_code || '',
          quantity:    i.quantity   != null ? String(parseFloat(i.quantity))   : '',
          unit:        i.unit || 'PCS',
          unit_price:  i.unit_price != null ? String(parseFloat(i.unit_price)) : '',
          amount:      i.amount     != null ? parseFloat(i.amount).toFixed(2)  : '',
        })));
      }
      setError('');
    } catch {
      setError('Could not load that proforma invoice');
    }
  };

  // Deep link from the Proforma Invoices list: /purchase-orders/new?from_pi=<id>
  useEffect(() => {
    if (!id && fromPi) applyProforma(fromPi);
  }, [id, fromPi]);

  const updateItem = (idx, field, val) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: val };
    if (field === 'quantity' || field === 'unit_price') {
      const qty = parseFloat(field === 'quantity' ? val : updated[idx].quantity) || 0;
      const price = parseFloat(field === 'unit_price' ? val : updated[idx].unit_price) || 0;
      updated[idx].amount = (qty * price).toFixed(2);
    }
    setItems(updated);
    // Recalculate INR if rate set
    if (form.exchange_rate) {
      const total = updated.reduce((s, i) => s + parseFloat(i.amount || 0), 0);
      setForm(f => ({ ...f, inr_amount: (total * parseFloat(f.exchange_rate || 0)).toFixed(2) }));
    }
  };

  const addItem = () => setItems([...items, { ...EMPTY_ITEM }]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const total = items.reduce((s, i) => s + parseFloat(i.amount || 0), 0);

  const handleExchangeRate = (rate) => {
    setForm({ ...form, exchange_rate: rate, inr_amount: rate ? (total * parseFloat(rate)).toFixed(2) : '' });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.po_number) { setError('PO number is required'); return; }
    if (!form.beneficiary_id) { setError('Please select a beneficiary / supplier'); return; }
    if (total === 0) { setError('Add at least one line item with a non-zero amount'); return; }

    setLoading(true);
    try {
      const payload = { ...form, total_amount: total.toFixed(2), items };
      if (id) await api.put(`/purchase-orders/${id}`, payload);
      else await api.post('/purchase-orders', payload);
      navigate('/purchase-orders');
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving PO');
    } finally { setLoading(false); }
  };

  const f = (field) => ({ value: form[field], onChange: (e) => setForm({ ...form, [field]: e.target.value }) });

  return (
    <Box component="form" onSubmit={handleSave}>
      <PageHeader
        title={id ? 'Edit PO' : (sourcePi ? 'Create PO from Proforma' : 'Create PO')}
        subtitle="Purchase order issued to supplier"
        breadcrumbs={[{ label: 'Purchase Orders', path: '/purchase-orders' }, { label: id ? 'Edit' : 'New PO' }]}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => navigate('/purchase-orders')}>Cancel</Button>
            <Button type="submit" variant="contained" startIcon={<Save />} disabled={loading}>
              {loading ? 'Saving...' : 'Save PO'}
            </Button>
          </Box>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2.5}>
        {/* Left: PO details */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 2.5 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>PO Details</Typography>
              <Grid container spacing={2}>
                {!id && (
                  <Grid item xs={12}>
                    <TextField label="Create from Proforma Invoice" select fullWidth value={sourcePi}
                      onChange={(e) => applyProforma(e.target.value)}
                      helperText="Copies the supplier, terms and all line items from that proforma. The PO number and date below stay as they are.">
                      <MenuItem value=""><em>— Start blank —</em></MenuItem>
                      {proformas.map(p => (
                        <MenuItem key={p.id} value={String(p.id)}>
                          {p.invoice_number} · {p.invoice_date} · {p.currency} {parseFloat(p.total_amount || 0).toFixed(2)} · {p.beneficiary_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <TextField label="PO Number" fullWidth {...f('po_number')}
                    InputProps={{ readOnly: !id }}
                    helperText={!id ? 'Auto-generated' : undefined} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="PO Date *" type="date" fullWidth {...f('po_date')} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Estimated Ship Date" type="date" fullWidth {...f('ship_date')} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Currency" select fullWidth {...f('currency')}>
                    {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Beneficiary / Supplier *" select fullWidth value={form.beneficiary_id}
                    onChange={(e) => setForm({ ...form, beneficiary_id: e.target.value })}>
                    {beneficiaries.map(b => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Purpose of Remittance" select fullWidth {...f('purpose_of_remittance')}>
                    {PURPOSES.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Payment Terms" fullWidth {...f('payment_terms')}
                    placeholder="e.g. 100% in Advance" />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Notes / Description" fullWidth multiline rows={2} {...f('notes')} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Items table */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1">Line Items</Typography>
                <Button size="small" startIcon={<Add />} onClick={addItem} variant="outlined">Add Item</Button>
              </Box>
              <Box sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 240 }}>Description</TableCell>
                    <TableCell sx={{ minWidth: 240 }}>SKU<Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10 }}>(internal, not on PDF)</Typography></TableCell>
                    <TableCell sx={{ minWidth: 130 }}>HS Code</TableCell>
                    <TableCell sx={{ minWidth: 110 }}>Qty</TableCell>
                    <TableCell sx={{ minWidth: 110 }}>Unit</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>Unit Price ({form.currency})</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>Amount</TableCell>
                    <TableCell sx={{ width: 48 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <TextField fullWidth value={item.description}
                          onChange={(e) => updateItem(idx, 'description', e.target.value)}
                          placeholder="e.g. Proximity tags (55x18mm)" />
                      </TableCell>
                      <TableCell>
                        <TextField fullWidth value={item.sku || ''}
                          onChange={(e) => updateItem(idx, 'sku', e.target.value)}
                          placeholder="e.g. PT-55-18" />
                      </TableCell>
                      <TableCell>
                        <TextField fullWidth value={item.hsn_code}
                          onChange={(e) => updateItem(idx, 'hsn_code', e.target.value)}
                          placeholder="85235910" />
                      </TableCell>
                      <TableCell>
                        <TextField type="number" fullWidth value={item.quantity}
                          onChange={(e) => updateItem(idx, 'quantity', e.target.value)} />
                      </TableCell>
                      <TableCell>
                        <TextField select fullWidth value={item.unit}
                          onChange={(e) => updateItem(idx, 'unit', e.target.value)}>
                          {['PCS', 'NOS', 'KGS', 'MTR', 'SET', 'UNT'].map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField type="number" fullWidth value={item.unit_price}
                          onChange={(e) => updateItem(idx, 'unit_price', e.target.value)}
                          inputProps={{ step: '0.0001' }} />
                      </TableCell>
                      <TableCell>
                        <TextField type="number" fullWidth value={item.amount}
                          onChange={(e) => updateItem(idx, 'amount', e.target.value)} />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => removeItem(idx)} disabled={items.length === 1}>
                          <Delete fontSize="small" sx={{ color: items.length > 1 ? 'error.main' : 'text.disabled' }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right: Summary */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: 'sticky', top: 80 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>PO Summary</Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                <Typography variant="body2" fontWeight={600}>{form.currency} {total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</Typography>
              </Box>
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle2">Total Amount</Typography>
                <Typography variant="h6" color="primary.main" fontWeight={700}>
                  {form.currency} {total.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </Typography>
              </Box>

              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>INR Conversion</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>(Internal reference only — not printed on PO)</Typography>
              <TextField
                label="Exchange Rate (1 USD = INR)"
                type="number"
                fullWidth
                size="small"
                value={form.exchange_rate}
                onChange={(e) => handleExchangeRate(e.target.value)}
                sx={{ mb: 1.5 }}
                inputProps={{ step: '0.01' }}
                placeholder="e.g. 86.50"
              />
              {form.inr_amount && (
                <Box sx={{ p: 1.5, bgcolor: 'rgba(26, 58, 219, 0.10)', borderRadius: 2, mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">INR Equivalent</Typography>
                  <Typography variant="subtitle1" fontWeight={700} color="primary.main">
                    ₹ {parseFloat(form.inr_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />
              <Button type="submit" variant="contained" fullWidth size="large" disabled={loading} startIcon={<Save />}>
                {loading ? 'Saving...' : id ? 'Update PO' : 'Create PO'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
